import { apiBuilder, prefixApi } from "@zodios/core";
import { z } from "zod";
import { columnFilterSchema } from "../../../common/columnFilterSchema";
import { cursorSchema } from "../../../common/cursorSchema";
import { statsSchema } from "../../../common/statsSchema";
import { tagsSchema, tagsEnhancedSchema } from "../../../common/tagsSchema";

const targetStatsSchema = z.object({
  top_of_search_impression_share: z.number().nullable(),
  top_of_search_impression_share_prev: z.number().nullable(),
  top_of_search_impression_share_delta: z.number().nullable(),
  top_of_search_impression_share_percent: z.number().nullable(),
});

export const targetReportApiDefinitions = prefixApi(
  "/api",
  apiBuilder({
    alias: "getV4TargetReportByTarget",
    method: "get",
    // target_keyword_report param is dummy to not take up the generic URL with this query
    path: "/v4/query/?target_keyword_report=true",
    parameters: [
      {
        name: "search",
        type: "Query",
        schema: z.string().array().optional(),
      },
      {
        name: "marketplace",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "table",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "grouping",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "start_date",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "end_date",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "start_date_previous",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "end_date_previous",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "filters",
        type: "Query",
        schema: columnFilterSchema,
      },
      {
        name: "cursor",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    status: 200,
    response: z.object({
      data: z.array(
        z
          .object({
            adv_keyword_id: z.number().optional(),
            adv_target_id: z.number().optional(),
            creation_date: z.string().date(),
            // Below is to parse targeting expressions for advertised targets
            text: z.string().transform((targetingExpression) => {
              try {
                return JSON.parse(targetingExpression);
              } catch {
                return targetingExpression;
              }
            }),
            match_type: z.string(),
            bid: z.number(),
            bid_resolved: z.number(),
            preview_bid: z.number().nullable().optional(),
            bid_trend_current: z.literal("Decreasing").or(z.literal("Increasing")).or(z.literal("Stable")),
            bid_trend_latest: z.literal("Decreasing").or(z.literal("Increasing")).or(z.literal("Stable")),
            bid_trend_slope: z.literal("Decreasing").or(z.literal("Increasing")).or(z.literal("Stable")),
            manual_bid: z.number().nullable(),
            has_manual_bid: z.boolean().nullable().optional(),
            floor_bid: z.number(),
            ceil_bid: z.number(),
            state: z.string(),
            target_bid_state: z.number().nullable().optional(),
            ad_id: z.number(),
            ad_status: z.number().optional(),
            ad_state: z.number().optional(),
            category_id: z.number().optional(),
            ad_group_id: z.string().optional(),
            ad_group_name: z.string().optional(),
            source: z.string(),
            tags: tagsSchema.nullable(),
            tags_enhanced: tagsEnhancedSchema,
            scheduled_actions: z.array(
              z.object({
                scheduled_action_id: z.number(),
                scheduled_action_start_date: z.string().optional(),
                scheduled_action_end_date: z.string().optional(),
                scheduled_action_fields: z.string().transform((spec) => {
                  try {
                    return JSON.parse(spec) as {
                      field_name?: string;
                      change_value?: number;
                    }[];
                  } catch {
                    return [];
                  }
                }),
                scheduled_action_status: z.string().optional(),
              }),
            ),
            refinements: z
              .string()
              .transform((refinements) => {
                try {
                  return JSON.parse(refinements) as string[];
                } catch {
                  return {};
                }
              })
              .nullable()
              .optional(),
            lockout: z.boolean().optional(),
          })
          .merge(statsSchema)
          .merge(targetStatsSchema),
      ),
      cursor: cursorSchema,
      count: z.number(),
      count_details: z
        .object({
          locked_count: z.number(),
        })
        .optional(),
    }),
  })
    .addEndpoint({
      alias: "getV4TargetReportByDate",
      method: "get",
      path: "/v4/query/?grouping=start_date&limit=1000000",
      parameters: [
        {
          name: "search",
          type: "Query",
          schema: z.string().array().optional(),
        },
        {
          name: "table",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "start_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "date_group",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "filters",
          type: "Query",
          schema: columnFilterSchema,
        },
      ],
      status: 200,
      response: z.object({
        data: z.array(
          z
            .object({
              start_date: z.string().date(),
            })
            .merge(statsSchema)
            .merge(targetStatsSchema),
        ),
        cursor: cursorSchema,
        count: z.number(),
      }),
    })
    .addEndpoint({
      alias: "getV4TargetReportTotals",
      method: "get",
      path: "/v4/query/aggregate/",
      parameters: [
        {
          name: "search",
          type: "Query",
          schema: z.string().array().optional(),
        },
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "table",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "start_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "filters",
          type: "Query",
          schema: columnFilterSchema,
        },
      ],
      status: 200,
      response: z.object({
        data: statsSchema.merge(targetStatsSchema),
      }),
    })
    .addEndpoint({
      alias: "getV4TargetReportExport",
      method: "get",
      path: "/v4/query/?xls=true",
      parameters: [
        {
          name: "table",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "report_type",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "start_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "filters",
          type: "Query",
          schema: columnFilterSchema,
        },
      ],
      status: 200,
      response: z.instanceof(Blob),
    })
    .addEndpoint({
      alias: "getV4KeywordReportOptions",
      method: "get",
      path: "/v4/query/values/?table=keyword",
      parameters: [
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
      ],
      status: 200,
      response: z.object({
        data: z.object({
          tags: z.array(z.string()),
          locked_ids: z.array(z.coerce.number()),
        }),
      }),
    })
    .addEndpoint({
      alias: "getV4TargetReportOptions",
      method: "get",
      path: "/v4/query/values/?table=target",
      parameters: [
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
      ],
      status: 200,
      response: z.object({
        data: z.object({
          tags: z.array(z.string()),
          locked_ids: z.array(z.coerce.number()),
        }),
      }),
    })
    .build(),
);
